import { getFlag } from '@nx/launch-darkly-service';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSelector, createSlice } from '@reduxjs/toolkit';

export type AppContextSliceState = {
  activeOrgId: string | null;
  activeProjectId: string | null;
  userId: string | null;
};

export interface AppState extends AppContextSliceState {
  activeProjectId: string;
}

const initialState: AppContextSliceState = {
  activeOrgId: null,
  activeProjectId: null,
  userId: null,
};

export const slice = createSlice({
  name: 'appContext',
  initialState,
  reducers: {
    activeOrgSelected(state: AppContextSliceState, action: PayloadAction<AppState['activeOrgId']>) {
      if (action.payload !== state.activeOrgId) {
        state.activeOrgId = action.payload;
        state.activeProjectId = null;
      }
    },
    activeProjectSelected(
      state: AppContextSliceState,
      action: PayloadAction<{
        orgId: NonNullable<AppContextSliceState['activeOrgId']>;
        projectId: AppContextSliceState['activeProjectId'];
      }>,
    ) {
      state.activeOrgId = action.payload.orgId;
      state.activeProjectId = action.payload.projectId;
    },
    userIdSelected(state: AppContextSliceState, action: PayloadAction<AppState['userId']>) {
      state.userId = action.payload;
    },
  },
  selectors: {
    selectCurrentDestination: createSelector(
      (state: AppContextSliceState) => state.activeOrgId,
      (state: AppContextSliceState) => state.activeProjectId,
      (activeOrgId, activeProjectId) => {
        if (activeOrgId !== null && activeProjectId !== null) {
          return [activeOrgId, activeProjectId] as const;
        }

        if (activeOrgId !== null) {
          return [activeOrgId, null] as const;
        }

        return [null, null] as const;
      },
    ),
    selectHomePath(state) {
      // This not the best approach since this is a side-effect which breaks Redux best-practice. But the value never
      // changes so it should work fine
      const homePage = getFlag('plgHomePageTypes', 'none');
      if (state.activeOrgId !== null && state.activeProjectId !== null) {
        if (homePage !== 'none') {
          return `/projects/${state.activeProjectId}/home`;
        }
        return `/projects/${state.activeProjectId}/instances`;
      }

      if (state.activeOrgId !== null) {
        return `/orgs/${state.activeOrgId}/projects`;
      }

      return null;
    },
    selectUserId(state) {
      return state.userId;
    },
  },
});

export const isAppContextState = (state: unknown): state is { appContext: AppContextSliceState } => {
  return state !== null && typeof state === 'object' && slice.name in state;
};

export const {
  activeOrgSelected: setActiveOrg,
  activeProjectSelected: setActiveProject,
  userIdSelected: setUserId,
} = slice.actions;

export const { selectCurrentDestination, selectHomePath, selectUserId } = slice.selectors;

export default slice.reducer;
